import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Users, ChevronDown, Check } from 'lucide-react';
import companyStore from '../../store/company-store';
import { t } from 'i18next';

interface CollaboratorSelectProps {
  value: string;
  onChange: (collaboratorId: string) => void;
  disabled?: boolean;
}

const CollaboratorSelect: React.FC<CollaboratorSelectProps> = observer(({
  value,
  onChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Charger les collaborateurs au montage
  useEffect(() => {
    const companyId = companyStore.getIdCompanySelectedDefault;
    if (companyId) {
      companyStore.setCompanyById(companyId);
    }
  }, []);

  // Sélection automatique si un seul collaborateur
  useEffect(() => {
    const collaborators = companyStore.selectedCompany?.collaboratorCompanies || [];
    if (collaborators.length === 1 && !value && !disabled) {
      onChange(collaborators[0].collaboratorCompagnieId);
    }
  }, [companyStore.selectedCompany?.collaboratorCompanies, value, onChange, disabled]);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const collaborators = companyStore.selectedCompany?.collaboratorCompanies || [];

  // Trouver le collaborateur sélectionné (value = collaboratorCompagnieId)
  const selectedCollaborator = collaborators.find(c => c.collaboratorCompagnieId === value);

  // Générer les initiales pour l'avatar
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Générer une couleur de fond basée sur le nom
  const getAvatarColor = (firstName: string, lastName: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500',
    ];
    const index = (firstName.charCodeAt(0) + lastName.charCodeAt(0)) % colors.length;
    return colors[index];
  };

  const handleSelect = (collaboratorId: string) => {
    onChange(collaboratorId);
    setIsOpen(false);
  };

  // Si un seul collaborateur, ne pas afficher le select
  if (collaborators.length === 1) {
    return null;
  }

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Collaborateur
        <span className="text-red-500 ml-1">*</span>
      </label>

      <div ref={dropdownRef} className="relative">
        {/* Bouton de sélection */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full flex items-center justify-between gap-2 px-3 py-2
            bg-white dark:bg-gray-800
            border border-gray-300 dark:border-gray-700
            rounded-lg shadow-sm
            transition-all duration-200
            ${!disabled && 'hover:border-primary hover:shadow'}
            ${isOpen && 'border-primary ring-1 ring-primary/20'}
            ${disabled && 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-900'}
          `}
        >
          {selectedCollaborator ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Avatar */}
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center
                text-white font-semibold text-xs flex-shrink-0
                ${getAvatarColor(selectedCollaborator.firstName, selectedCollaborator.lastName)}
              `}>
                {getInitials(selectedCollaborator.firstName, selectedCollaborator.lastName)}
              </div>

              {/* Nom */}
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {selectedCollaborator.firstName} {selectedCollaborator.lastName}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-gray-400" />
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('dashboard.selectCollaborator', 'Sélectionner un collaborateur')}
              </span>
            </div>
          )}

          <ChevronDown
            className={`
              w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0
              ${isOpen ? 'transform rotate-180' : ''}
            `}
          />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="
            absolute z-50 w-full mt-2
            bg-white dark:bg-gray-800
            border border-gray-200 dark:border-gray-700
            rounded-xl shadow-xl
            overflow-hidden
          ">
            <div className="max-h-80 overflow-y-auto p-2">
              {collaborators.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Aucun collaborateur disponible
                  </p>
                </div>
              ) : (
                collaborators.map((collaborator) => {
                  const isSelected = collaborator.collaboratorCompagnieId === value;

                  return (
                    <button
                      key={collaborator.collaboratorCompagnieId}
                      type="button"
                      onClick={() => handleSelect(collaborator.collaboratorCompagnieId)}
                      className={`
                        w-full flex items-center gap-2 px-2 py-2 rounded-lg
                        transition-all duration-150
                        ${isSelected
                          ? 'bg-primary/10 dark:bg-primary/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }
                      `}
                    >
                      {/* Avatar */}
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center
                        text-white font-semibold text-xs flex-shrink-0
                        ${getAvatarColor(collaborator.firstName, collaborator.lastName)}
                      `}>
                        {getInitials(collaborator.firstName, collaborator.lastName)}
                      </div>

                      {/* Informations */}
                      <div className="flex-1 text-left min-w-0">
                        <p className={`
                          text-sm font-medium truncate
                          ${isSelected
                            ? 'text-primary dark:text-primary-400'
                            : 'text-gray-900 dark:text-white'
                          }
                        `}>
                          {collaborator.firstName} {collaborator.lastName}
                        </p>
                      </div>

                      {/* Icône de sélection */}
                      {isSelected && (
                        <Check className="w-4 h-4 text-primary dark:text-primary-400 flex-shrink-0" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default CollaboratorSelect;
